import { pathOr, reverse } from 'ramda'

import { cannot } from '@/app/helpers'
import { mapIndexed } from '@/app/module/campaigns/helpers'
import { CampaignSummary, CampaignTemplateType, PartSummary } from '@/app/module/campaigns/types'
import { currencyFormat } from '@/app/service/util/format'

import { BarChartDatum } from './components/overview-bar-chart'
import { PieChartDatum } from './components/overview-pie-chart'

export const getPartStats = (message: PartSummary) => {
  switch (true) {
    case 'message' in message && !!message.message: {
      const { deliveredToPhoneConfirmed, deliveredToPhoneFailed, deliveredToPhoneUnknown, deliveredToTelcoFailed } =
        message.message
      return {
        engaged: deliveredToPhoneConfirmed + deliveredToPhoneUnknown,
        notEngaged: deliveredToPhoneFailed,
        error: deliveredToTelcoFailed,
      }
    }
    case 'voice' in message && !!message.voice: {
      const { answered, failedToRing, notAnswered } = message.voice
      return {
        engaged: answered,
        notEngaged: notAnswered,
        error: failedToRing,
      }
    }
    case 'whatsapp' in message && !!message.whatsapp: {
      const { failed, inProgress, success } = message.whatsapp
      return {
        engaged: success,
        notEngaged: inProgress,
        error: failed,
      }
    }

    default:
      break
  }

  return {
    engaged: 0,
    notEngaged: 0,
    error: 0,
  }
}

export const processPartsData = (partStats: PartSummary[] = []) =>
  reverse(
    mapIndexed((message: PartSummary, i) => {
      const messageData = {
        name: `${i + 1}. ${message.name}`,
        id: message.id,
        ...getPartStats(message),
      }
      return messageData
    }, partStats),
  )

export const processStatsForSummary = ({ itemSummary, item }: any) => {
  const { runs } = item
  const { overallMessageStats, overallVoiceStats } = itemSummary
  return {
    subscriptions: runs,
    engaged: pathOr(0, ['total'], overallMessageStats) + pathOr(0, ['total'], overallVoiceStats),
    calls: pathOr(0, ['total'], overallVoiceStats),
    messages: pathOr(0, ['total'], overallMessageStats),
    cost: {
      message: currencyFormat(pathOr('0', ['overallMessageStats', 'costHuman'], itemSummary)),
      topup: currencyFormat(pathOr('0', ['overallAirtimeStats', 'costHuman'], itemSummary)),
      voice: currencyFormat(pathOr('0', ['overallVoiceStats', 'costHuman'], itemSummary)),
      whatsapp: currencyFormat(pathOr('0', ['overallWhatsappStats', 'costHuman'], itemSummary)),
      total: currencyFormat(pathOr('0', ['costHuman'], itemSummary)),
    },
  }
}

export function getChartData(
  summary: CampaignSummary,
  type: CampaignTemplateType.CATI | CampaignTemplateType.SMSBlast | CampaignTemplateType.VoiceSurvey,
): PieChartDatum[]
export function getChartData(
  summary: CampaignSummary,
  type:
    | CampaignTemplateType.Drip
    | CampaignTemplateType.Reminder
    | CampaignTemplateType.SMSSurvey
    | CampaignTemplateType.WhatsAppSurvey,
): BarChartDatum[]
export function getChartData(
  summary: CampaignSummary,
  type:
    | CampaignTemplateType.CATI
    | CampaignTemplateType.SMSBlast
    | CampaignTemplateType.VoiceSurvey
    | CampaignTemplateType.Drip
    | CampaignTemplateType.Reminder
    | CampaignTemplateType.SMSSurvey
    | CampaignTemplateType.WhatsAppSurvey,
): BarChartDatum[] | PieChartDatum[] | [] {
  switch (type) {
    case CampaignTemplateType.CATI:
    case CampaignTemplateType.VoiceSurvey: {
      const {
        overallVoiceStats: { answered, failedToRing, notAnswered },
      } = summary
      return [
        {
          legendLabel: 'Picked Up',
          value: answered,
        },
        {
          legendLabel: 'Not Picked Up',
          value: notAnswered,
        },
        {
          legendLabel: 'Failed',
          value: failedToRing,
        },
      ]
    }

    case CampaignTemplateType.SMSBlast: {
      const {
        overallMessageStats: {
          deliveredToPhoneConfirmed,
          deliveredToPhoneFailed,
          deliveredToTelcoFailed,
          deliveredToPhoneUnknown,
        },
      } = summary
      return [
        {
          legendLabel: 'Contacts Engaged',
          value: deliveredToPhoneConfirmed + deliveredToPhoneUnknown,
        },
        {
          legendLabel: 'Contacts Not Engaged',
          value: deliveredToPhoneFailed,
        },
        {
          legendLabel: 'Error',
          value: deliveredToTelcoFailed,
        },
      ]
    }

    case CampaignTemplateType.Drip:
    case CampaignTemplateType.Reminder:
    case CampaignTemplateType.SMSSurvey:
    case CampaignTemplateType.WhatsAppSurvey:
      return processPartsData(summary.partStats)

    default:
      return cannot(type)
  }
}
